import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { TestTargetReady, UserInfoReady } from 'constants/events';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import SampleProductsUtils from 'utils/sampleProducts/SampleProducts';
import { SIMPLIFIED_PDP_COMPARE } from 'constants/TestTarget';
import { RESULTS_COUNT } from 'constants/constructorConstants';
import { CHANEL, SEPHORA_COLLECTION } from 'constants/brands';
import { showHighlightLayerWithPDP } from 'viewModel/selectors/testTarget/showHighlightLayerWithPDP';
import { isShowSMNEnabledSelector } from 'viewModel/selectors/testTarget/showSMNSelector';
import { showAddDropDownReviewSectionSelector } from 'viewModel/selectors/testTarget/showAddDropDownReviewSectionSelector';
import { showAtbCarouselsPDPSelector } from 'viewModel/selectors/testTarget/showAtbCarouselsPDPSelector';
import { showReorderPageSectionPdpSelector } from 'viewModel/selectors/testTarget/showReorderPageSectionPdpSelector';
import { showSocialProofPDPSelector } from 'viewModel/selectors/testTarget/showSocialProofPDPSelector';
import { showSmnCaPpageCarouselSelector } from 'viewModel/selectors/testTarget/showSmnCaPpageCarouselSelector';
import { showSmnCaPpageBannerSelector } from 'viewModel/selectors/testTarget/showSmnCaPpageBannerSelector';
import { showIncreaseATBButtonOnPdpSelector } from 'viewModel/selectors/testTarget/showIncreaseATBButtonOnPdpSelector';
import { showProductDisclosuresSelector } from 'viewModel/selectors/testTarget/showProductDisclosuresSelector';
import { showStickyNavBarPdpSelector } from 'viewModel/selectors/testTarget/showStickyNavBarPdpSelector';
import { showCollapseHighlightsSectionPdpSelector } from 'viewModel/selectors/testTarget/showCollapseHighlightsSectionPdpSelector';
import { showPDPTopOfPageRedesignSelector } from 'viewModel/selectors/testTarget/showPDPTopOfPageRedesignSelector';
import { showPDPPercentRecommendedSelector } from 'viewModel/selectors/testTarget/showPDPPercentRecommendedSelector';
import { showPDPVideoIconSelector } from 'viewModel/selectors/testTarget/showPDPVideoIconSelector';
import { showAddAssuranceZonePdpSelector } from 'viewModel/selectors/testTarget/showAddAssuranceZonePdpSelector';
import { showDiscountedPriceWithCodeSelector } from 'viewModel/selectors/testTarget/showDiscountedPriceWithCodeSelector';
import ProductActions from 'actions/ProductActions';
import productSamplesActions from 'actions/ProductSamplesActions';
import safelyReadProp from 'analytics/utils/safelyReadProperty';
import skuUtils from 'utils/Sku';
import localeUtils from 'utils/LanguageLocale';
import userUtils from 'utils/User';
import Location from 'utils/Location';
import Empty from 'constants/empty';
import basketConstants from 'constants/Basket';
import constants from 'constants/content';
import Storage from 'utils/localStorage/Storage';
import agentAwareUtils from 'utils/AgentAware';
import SessionAI from 'services/api/thirdparty/SessionAI';
import bazaarVoiceApi from 'services/api/thirdparty/BazaarVoice';
import BPUtils from 'utils/BeautyPreferences';
import { mediaQueries, space } from 'style/config';
import UI from 'utils/UI';
import anaConsts from 'analytics/constants';
import anaUtils from 'analytics/utils';
import { mountBannerEventData } from 'analytics/utils/eventName';
import RMN_BANNER_TYPES from 'components/Rmn/constants';
import RmnAndPlaUtils from 'utils/rmnAndPla';
import RmnMidPageBanner from 'components/Rmn/RmnMidPageBanner';
import ConstructorCarousel from 'components/ConstructorCarousel';
import RecServiceCarousel from 'components/RecServiceCarousel';
import RwdProductPageEN from 'components/ProductPage/locales/RwdProductPage_en_US';
import RwdProductPageFR from 'components/ProductPage/locales/RwdProductPage_fr_CA';
import usePageLayout from 'components/ProductPage/Type/RegularProduct/hooks/usePageLayout';
import usePageAnalytics from 'components/ProductPage/Type/RegularProduct/hooks/usePageAnalytics';
import useRmnBanners from 'components/ProductPage/Type/RegularProduct/hooks/useRmnBanners';
import useFulfillment from 'components/ProductPage/Type/RegularProduct/hooks/useFulfillment';
import useCommerce from 'components/ProductPage/Type/RegularProduct/hooks/useCommerce';

const { CUSTOM_ACTION_TYPES } = constants;
const { BasketType } = basketConstants;
const BRAND_ID_CHANEL = '1065';
const { POSITIONS, SECTIONS, TYPES } = RMN_BANNER_TYPES;
const getText = localeUtils.getLocalisedDataFromResource(RwdProductPageEN, RwdProductPageFR);

/* eslint-disable complexity */
const useRegularProductProps = processSkuId => {
    const dispatch = useDispatch();
    const currentProduct = useSelector(state => state.page.product, shallowEqual);
    const testTarget = useSelector(state => state.testTarget);
    const preferredStoreInfo = useSelector(state => state.user.preferredStoreInfo, shallowEqual);
    const userSkinTones = useSelector(state => state.user?.beautyInsiderAccount?.skinTones, shallowEqual);

    const highlightLayerWithPDP = useSelector(showHighlightLayerWithPDP);
    const showSMNEnabled = useSelector(isShowSMNEnabledSelector);
    const showAddDropDownReviewSection = useSelector(showAddDropDownReviewSectionSelector);
    const isCarouselsATBEnabled = useSelector(showAtbCarouselsPDPSelector);
    const reorderPageSectionPdp = useSelector(showReorderPageSectionPdpSelector);
    const showSocialProofPDP = useSelector(showSocialProofPDPSelector);
    const showSmnCaPpageCarousel = useSelector(showSmnCaPpageCarouselSelector);
    const showSmnCaPpageBanner = useSelector(showSmnCaPpageBannerSelector);
    const increaseATBButtonOnPdp = useSelector(showIncreaseATBButtonOnPdpSelector);
    const showProductDisclosures = useSelector(showProductDisclosuresSelector);
    const stickyNavBarPdp = useSelector(showStickyNavBarPdpSelector);
    const collapseHighlightsSectionPdp = useSelector(showCollapseHighlightsSectionPdpSelector);
    const showPDPTopOfPageRedesign = useSelector(showPDPTopOfPageRedesignSelector);
    const showPDPPercentRecommended = useSelector(showPDPPercentRecommendedSelector);
    const showPDPVideoIcon = useSelector(showPDPVideoIconSelector);
    const showAddAssuranceZonePdp = useSelector(showAddAssuranceZonePdpSelector);
    const showDiscountedPriceWithCode = useSelector(showDiscountedPriceWithCodeSelector);

    const { isSmallView } = usePageLayout();
    const { preventPageRenderReport } = usePageAnalytics();
    const rmnBanners = useRmnBanners();
    const fulfillment = useFulfillment();
    const commerce = useCommerce();

    const [isSkuReady, setIsSkuReady] = useState(false);
    const [regularProductHasMounted, setRegularProductHasMounted] = useState(false);
    const [profileColorIQ, setProfileColorIQ] = useState(undefined);
    const [isProductSampleEnabled, setIsProductSampleEnabled] = useState(false);
    const [showPdpCompare, setShowPdpCompare] = useState(false);
    const [simplifiedPdpCompareExperience, setSimplifiedPdpCompareExperience] = useState(undefined);
    const [displayOrderCutoffCountdown, setDisplayOrderCutoffCountdown] = useState(undefined);
    const [showHighlyRatedFor] = useState(true);
    const [isSentimentSelectedFromHighlyRated, setIsSentimentSelectedFromHighlyRated] = useState(false);
    const [reviewStatistics, setReviewStatistics] = useState(undefined);
    const [initialTotalReviews, setInitialTotalReviews] = useState(undefined);
    const [showProsAndCons, setShowProsAndCons] = useState(true);
    const [, setShowCarousel] = useState(false);

    const matchCallCounterRef = useRef(0);
    const hasProductMatchFetchedRef = useRef(false);
    const bvStatsInFlightRef = useRef(false);
    const prevProductIdRef = useRef(currentProduct?.productDetails?.productId);

    useEffect(() => {
        processSkuId({
            get isSkuReady() {
                return isSkuReady;
            },
            set isSkuReady(val) {
                setIsSkuReady(val);
            }
        });
        setRegularProductHasMounted(true);

        const hash = window.location.hash.replace('#', '');

        if (hash) {
            const tryScroll = () => {
                const target = document.getElementById(hash);

                if (target) {
                    target.scrollIntoView({ inline: 'center', block: 'start', behavior: 'smooth' });
                } else {
                    setTimeout(tryScroll, 2000);
                }
            };

            tryScroll();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchInitialReviewStats = (force = false) => {
        if (bvStatsInFlightRef.current) {
            return;
        }

        const productId = currentProduct?.productDetails?.productId;
        const isBV = Sephora.configurationSettings.isBazaarVoiceEnabled;

        if (!isBV || !productId || !currentProduct?.reviewFilters) {
            return;
        }

        if (!force && reviewStatistics && (initialTotalReviews || initialTotalReviews === 0)) {
            return;
        }

        const refinements = BPUtils.getMasterListRefinementsByCategory(undefined);
        bvStatsInFlightRef.current = true;

        bazaarVoiceApi
            .getReviewsAndStatsWithConfig(currentProduct.reviewFilters, productId, 6, {}, 0, refinements)
            .then(data => {
                if (data && !data.apiFailed && data.reviewStatistics) {
                    setReviewStatistics(data.reviewStatistics);
                    setInitialTotalReviews(data.totalResults);
                    setShowProsAndCons(true);
                }
            })
            .finally(() => {
                bvStatsInFlightRef.current = false;
            });
    };

    useEffect(() => {
        const sku = currentProduct?.currentSku;
        const productId = currentProduct?.productDetails?.productId;
        const anyAvailable = !sku?.isOutOfStock || currentProduct?.regularChildSkus?.some(s => !s.isOutOfStock);
        const spEnabled =
            (localeUtils.isUS() && Sephora.configurationSettings.isSocialProofEnabled) ||
            (localeUtils.isCanada() && Sephora.configurationSettings.isSocialProofEnabledCan);

        if (
            spEnabled &&
            productId &&
            anyAvailable &&
            !skuUtils.isGiftCard(sku) &&
            !skuUtils.isSubscription(sku) &&
            !skuUtils.isSample(sku) &&
            !skuUtils.isBirthdayGift(sku)
        ) {
            SessionAI.getProductsCount(productId).then(data => dispatch(ProductActions.setProductViews(data)));
        }

        if (showAddDropDownReviewSection || (showPDPTopOfPageRedesign && showPDPPercentRecommended)) {
            fetchInitialReviewStats();
        }

        Sephora.Util.onLastLoadEvent(window, [UserInfoReady], () => {
            const sampleModalProduct = Storage.local.getItem(LOCAL_STORAGE.PDP_SAMPLES_MODAL);

            if (sampleModalProduct?.productId && currentProduct?.productDetails?.productId === sampleModalProduct.productId) {
                SampleProductsUtils.openSamplesModal(sampleModalProduct);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        Sephora.Util.onLastLoadEvent(window, [TestTargetReady], () => {
            const offers = testTarget?.offers || {};
            const cutoff = safelyReadProp('testTarget.offers.displayOrderCutoffCountdown.show', { testTarget });

            if (cutoff && !localeUtils.isFrench()) {
                setDisplayOrderCutoffCountdown(cutoff);
            }

            setShowPdpCompare(!!offers.pdpCompare?.show);
            setSimplifiedPdpCompareExperience(offers.simplifiedPdpCompare?.experience);
            setShowCarousel(Boolean(offers.PDPCarousel?.showCarousel));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [testTarget]);

    useEffect(() => {
        const newProductId = currentProduct?.productDetails?.productId;

        if (prevProductIdRef.current && newProductId && prevProductIdRef.current !== newProductId) {
            matchCallCounterRef.current = 0;
            hasProductMatchFetchedRef.current = false;

            if (showAddDropDownReviewSection || (showPDPTopOfPageRedesign && showPDPPercentRecommended)) {
                fetchInitialReviewStats(true);
            }

            if (!fulfillment.isBopisFirstOption) {
                fulfillment.selectShipped();
            }

            const sku = currentProduct?.currentSku;
            const anyAvailable = !sku?.isOutOfStock || currentProduct?.regularChildSkus?.some(s => !s.isOutOfStock);
            const spEnabled =
                (localeUtils.isUS() && Sephora.configurationSettings.isSocialProofEnabled) ||
                (localeUtils.isCanada() && Sephora.configurationSettings.isSocialProofEnabledCan);

            if (
                spEnabled &&
                anyAvailable &&
                !skuUtils.isGiftCard(sku) &&
                !skuUtils.isSubscription(sku) &&
                !skuUtils.isSample(sku) &&
                !skuUtils.isBirthdayGift(sku)
            ) {
                SessionAI.getProductsCount(newProductId).then(data => dispatch(ProductActions.setProductViews(data)));
            }
        }

        prevProductIdRef.current = newProductId;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentProduct?.productDetails?.productId]);

    const prevShowAddDropDownReviewSectionRef = useRef(showAddDropDownReviewSection);
    const prevPercentRecommendedGateRef = useRef(showPDPTopOfPageRedesign && showPDPPercentRecommended);
    useEffect(() => {
        const addDropDownFlagTurnedOn = !prevShowAddDropDownReviewSectionRef.current && showAddDropDownReviewSection;
        const currentPercentRecommendedGate = showPDPTopOfPageRedesign && showPDPPercentRecommended;
        const percentRecommendedGateTurnedOn = !prevPercentRecommendedGateRef.current && currentPercentRecommendedGate;

        if (addDropDownFlagTurnedOn || percentRecommendedGateTurnedOn) {
            fetchInitialReviewStats();
        }

        prevShowAddDropDownReviewSectionRef.current = showAddDropDownReviewSection;
        prevPercentRecommendedGateRef.current = currentPercentRecommendedGate;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showAddDropDownReviewSection, showPDPTopOfPageRedesign, showPDPPercentRecommended]);

    // (EXP-4078) BE does not add the current product to recentlyViewedSkus.
    // Prepend current sku (with category info) so the Recently Viewed Recap card on Home,
    // SiteSearch dropdown, and Recap component can include the just-visited PDP.
    useEffect(() => {
        const rvData = currentProduct?.recentlyViewedSkus;

        if (rvData) {
            const { currentSku, parentCategory } = currentProduct;
            const skuWithCategory = {
                ...currentSku,
                categoryId: parentCategory?.categoryId,
                categoryName: parentCategory?.displayName,
                categoryTargetUrl: parentCategory?.targetUrl
            };

            Storage.local.setItem('rvData', [skuWithCategory, ...rvData]);
        }
    }, [currentProduct]);

    useEffect(() => {
        if (!currentProduct?.currentSku) {
            return;
        }

        const biSkinTones = userSkinTones?.length;

        if (biSkinTones) {
            matchCallCounterRef.current = biSkinTones;
        }

        if (matchCallCounterRef.current >= 1 && skuUtils.showRWDColorIQOnPage(currentProduct) && !hasProductMatchFetchedRef.current) {
            skuUtils.getCurrentProductColorIQMatch(currentProduct).then(match => {
                hasProductMatchFetchedRef.current = true;
                setProfileColorIQ(match);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userSkinTones, currentProduct?.currentSku?.skuId]);

    const {
        hoveredSku, currentSku = {}, regularChildSkus, productDetails = {}, content = {}, hasProductSamples
    } = currentProduct || {};

    const focusedSku = hoveredSku || currentSku;
    const { isExternallySellable } = currentSku;
    const isGiftCard = skuUtils.isGiftCard(focusedSku);
    const showAssuranceZone = showAddAssuranceZonePdp && !isGiftCard && currentSku?.isReturnable !== false;
    const isCanada = localeUtils.isCanada();
    const isUsa = localeUtils.isUS();
    const isChanel = productDetails.brand?.brandId === BRAND_ID_CHANEL;
    const brandDisplayName = productDetails.brand?.displayName?.toLowerCase() || '';
    const isRmnExperience = brandDisplayName !== CHANEL;
    const bvEnabled = Sephora.configurationSettings.isBazaarVoiceEnabled;
    const isRatingsEnabled = bvEnabled && Sephora.configurationSettings.productPageConfigurations?.isPpageRatingsEnabled;
    const isQandAEnabled = bvEnabled && Sephora.configurationSettings.enableQandA;
    const hideProductTillSkuReady = !Sephora.configurationSettings.isShowProductBeforeSkuReadyEnabled ? !isSkuReady : false;
    const hasSentiments = currentProduct?.sentiments?.length > 0 && Sephora.configurationSettings.highlyRatedFor && showHighlyRatedFor;
    const displayFrequentlyBoughtTogether = brandDisplayName === SEPHORA_COLLECTION;
    const isChallengerOneFragrance = highlightLayerWithPDP?.challengerOne && skuUtils.isFragrance(currentProduct, currentSku);

    const basketType =
        fulfillment.selectedMethod === 'pickup'
            ? BasketType.BOPIS
            : fulfillment.selectedMethod === 'sameday'
                ? BasketType.SameDay
                : BasketType.Standard;

    const slotPrefix = isUsa ? '25' : '26';
    const params = { itemIds: productDetails.productId };
    const similarProductsCarouselParams = { ...params, numResults: RESULTS_COUNT.SIMILAR_PRODUCTS };

    const cleanBeautyLink = skuUtils.isCleanProduct(currentProduct?.specialProdCategories)
        ? {
            href: `/shop/${currentProduct.specialProdCategories[0].seoName}`,
            onClick: () => {
                const categoryName = currentProduct.specialProdCategories[0].seoName;
                Location.navigateTo(null, `/shop/${categoryName}`);
                anaUtils.setNextPageData({ linkData: `view more clean beauty:${categoryName}` });
            },
            children: getText('viewMoreCleanBeauty'),
            fontSize: localeUtils.isFrench() ? ['sm', 'base'] : 'base',
            'data-at': Sephora.debug.dataAt('view_more_clean_beauty_link')
        }
        : undefined;
    const isSimplifiedPdpCompareCh1 = simplifiedPdpCompareExperience === SIMPLIFIED_PDP_COMPARE.CHALLENGER_ONE;
    const isSimplifiedPdpCompareCh2 = simplifiedPdpCompareExperience === SIMPLIFIED_PDP_COMPARE.CHALLENGER_TWO;
    const isSimplifiedPdpCompareCh3 = simplifiedPdpCompareExperience === SIMPLIFIED_PDP_COMPARE.CHALLENGER_THREE;
    const isSimplifiedPdpCompareCh4 = simplifiedPdpCompareExperience === SIMPLIFIED_PDP_COMPARE.CHALLENGER_FOUR;
    const isSimplifiedPdpCompare = isSimplifiedPdpCompareCh1 || isSimplifiedPdpCompareCh2 || isSimplifiedPdpCompareCh3 || isSimplifiedPdpCompareCh4;

    let callToActionSku = currentSku;

    if (Sephora.isAgent && agentAwareUtils.isAgentAwareSession()) {
        callToActionSku = { ...currentSku, isAppExclusive: false };
    }

    let displayedProductBanner = {};
    let displayedMiddleProductBanner = {};
    let displayedBottomProductBanner = {};
    let shouldEnablePdpSample = false;
    let pdpSamplePacketImage = null;

    if (Object.keys(content).length) {
        displayedProductBanner = content.zone1?.[0] || {};
        displayedMiddleProductBanner = content.zone2?.[0] || {};
        displayedBottomProductBanner = content.zone3?.[0] || {};

        if (displayedMiddleProductBanner?.action?.actionType === CUSTOM_ACTION_TYPES.PDP_SAMPLE) {
            const pdpSamples = Sephora.configurationSettings.pdpSamples || Empty.Object;
            const { isPDPBannerCANEnabled = false, extendUsers = Empty.Object } = pdpSamples;
            const canShow = (isPDPBannerCANEnabled || isUsa) && (extendUsers.isEnabled || userUtils.isBI());

            if (Sephora.configurationSettings.isPDPSamplingEnabled && hasProductSamples && canShow) {
                shouldEnablePdpSample = true;
                pdpSamplePacketImage = displayedMiddleProductBanner?.media?.src;
            } else {
                displayedMiddleProductBanner = {};
            }
        }
    }

    // Dispatch sample product + flip isProductSampleEnabled flag when conditions match.
    // Matches OLD setProductSamplesBanner; kept out of render body to avoid re-render loop.
    useEffect(() => {
        if (shouldEnablePdpSample) {
            dispatch(
                productSamplesActions.setSampleProduct({
                    product: {
                        ...currentSku,
                        productId: productDetails.productId,
                        packetImage: pdpSamplePacketImage
                    }
                })
            );
            setIsProductSampleEnabled(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldEnablePdpSample, productDetails?.productId]);

    const brandData = { ...(productDetails.brand || currentProduct?.brand || {}) };
    delete brandData.ref;

    const atbStyles = {
        outOfStock: {
            [mediaQueries.lg]: {
                padding: `${space[3]}px ${space[8]}px`,
                width: 'auto',
                maxHeight: `${space[7]}px`
            }
        },
        inStock: {
            [mediaQueries.lg]: {
                padding: `${space[3]}px`,
                paddingRight: `${space[5]}px`,
                paddingLeft: `${space[8]}px`,
                maxHeight: `${space[7]}px`
            }
        }
    };

    const increaseATBButtonOnPdpStyle = increaseATBButtonOnPdp ? (currentSku?.isOutOfStock ? atbStyles.outOfStock : atbStyles.inStock) : undefined;

    const showRmnBannersFlag = showSMNEnabled && (isCanada ? isRmnExperience && showSmnCaPpageBanner : isRmnExperience);
    const showRmnCarousel = showSMNEnabled && (isCanada ? isRmnExperience && showSmnCaPpageCarousel : isRmnExperience);
    const displayQuantityPickerInATB = Location.isProductPage();
    const CarouselComponent = Sephora.configurationSettings.recsMigration?.isEnabled ? RecServiceCarousel : ConstructorCarousel;

    const scrollToQandA = e => {
        e.preventDefault();
        UI.scrollTo({ elementId: 'QandA' });
    };

    const renderRmnMidPageBanner = () => {
        if (!Object.keys(rmnBanners.rmnMidPageBannerPayload)?.length) {
            return null;
        }

        const slot = isSmallView ? `${slotPrefix}04223` : `${slotPrefix}04123`;
        const type = isSmallView ? TYPES.MOBILE_LEADERBOARD.NAME : TYPES.SUPER_LEADERBOARD.NAME;
        const clickEventName = mountBannerEventData({
            section: SECTIONS.MAIN,
            targetPage: anaConsts.RMN_PAGE_NAMES.pdp,
            type: anaConsts.EVENTS_TYPES_NAME.CLICK
        });
        const viewableEventName = mountBannerEventData({
            section: SECTIONS.MAIN,
            targetPage: anaConsts.RMN_PAGE_NAMES.pdp,
            type: anaConsts.EVENTS_TYPES_NAME.VIEW
        });
        const bannerProps = {
            slot,
            type,
            section: SECTIONS.MAIN,
            position: POSITIONS.TOP,
            source: RmnAndPlaUtils.RMN_SOURCES.PDP,
            clickEventName,
            viewableEventName
        };

        return (
            <RmnMidPageBanner
                position={POSITIONS.MID}
                rmnBannerPayload={rmnBanners.rmnMidPageBannerPayload}
                fallbackBannerPayload={rmnBanners.fallbackBannerPayload}
                bannerProps={bannerProps}
                bannerMobileProps={bannerProps}
            />
        );
    };

    const listPrice = focusedSku.listPrice ?? currentSku.listPrice;
    const salePrice = focusedSku.salePrice ?? currentSku.salePrice;
    const valuePrice = focusedSku.valuePrice ?? currentSku.valuePrice;
    const actionFlags = focusedSku.actionFlags ?? currentSku.actionFlags;

    const useItWithCarouselProps = {
        carouselContextId: productDetails.productId,
        title: currentProduct?.useItWithTitle || getText('useItWithTitle'),
        showAddToBasket: true,
        isHorizontal: true,
        skus: currentProduct?.ancillarySkus,
        analyticsContext: anaConsts.CONTEXT.USE_IT_WITH
    };

    const socialProofProps = {
        isSmallView,
        currentProduct,
        productDetails,
        currentSku,
        isSentimentSelectedFromHighlyRated,
        reviewStatistics,
        initialTotalReviews,
        showProsAndCons,
        showAddDropDownReviewSection
    };

    const priceProps = {
        isUsa,
        isCanada,
        isSmallView,
        isExternallySellable,
        isReplenishmentEligible: currentSku.isReplenishmentEligible,
        replenishMessage: () => commerce.replenishMessage(focusedSku),
        sku: { listPrice, salePrice, valuePrice, actionFlags }
    };

    return {
        currentProduct,
        currentSku,
        regularChildSkus,
        productDetails,
        focusedSku,
        isGiftCard,
        isCanada,
        isUsa,
        isChanel,
        isExternallySellable,
        isRatingsEnabled,
        isQandAEnabled,
        hasSentiments,
        hideProductTillSkuReady,
        basketType,
        isChallengerOneFragrance,
        displayFrequentlyBoughtTogether,
        isSimplifiedPdpCompareCh1,
        isSimplifiedPdpCompareCh2,
        isSimplifiedPdpCompareCh3,
        isSimplifiedPdpCompareCh4,
        isSimplifiedPdpCompare,
        slotPrefix,
        params,
        similarProductsCarouselParams,
        cleanBeautyLink,
        callToActionSku,
        brandData,
        increaseATBButtonOnPdpStyle,
        displayedProductBanner,
        displayedMiddleProductBanner,
        displayedBottomProductBanner,
        showRmnBanners: showRmnBannersFlag,
        showRmnCarousel,
        displayQuantityPickerInATB,
        CarouselComponent,
        isSmallView,
        preventPageRenderReport,
        fulfillment,
        commerce,
        rmnBanners,
        reorderPageSectionPdp,
        showSocialProofPDP,
        showAddDropDownReviewSection,
        isCarouselsATBEnabled,
        showProductDisclosures,
        stickyNavBarPdp,
        collapseHighlightsSectionPdp,
        showPDPTopOfPageRedesign,
        showPDPVideoIcon,
        showAssuranceZone,
        showDiscountedPriceWithCode,
        preferredStoreInfo,
        isSkuReady,
        regularProductHasMounted,
        profileColorIQ,
        isProductSampleEnabled,
        showPdpCompare,
        simplifiedPdpCompareExperience,
        displayOrderCutoffCountdown,
        isSentimentSelectedFromHighlyRated,
        setIsSentimentSelectedFromHighlyRated,
        reviewStatistics,
        initialTotalReviews,
        showProsAndCons,
        listPrice,
        salePrice,
        valuePrice,
        actionFlags,
        scrollToQandA,
        renderRmnMidPageBanner,
        useItWithCarouselProps,
        socialProofProps,
        priceProps
    };
};

export default useRegularProductProps;
